import React, {useEffect, useState} from 'react';
import Wrapper from '../../components/Wrapper';
import DashboardHeader from '../../components/DashboardHeader';
import './index.scss';
import Select from 'react-select';
import {Helmet} from 'react-helmet';
import {
  useCheckReportMutation,
  useLazyGetEquipmentQuery,
  useLazyGetFacilityQuery,
  useLazyGetFuelReceiptQuery,
  useLazyGetReportListQuery,
  useRemoveReportMutation,
  useReportPdfLinkMutation,
} from '../../store/builder/inspections.api';
import {useDispatch, useSelector} from 'react-redux';
import {setLoader} from '../../store/builder/reducer';
import {useNavigate} from 'react-router-dom';
import moment from 'moment';
import ReportModal from '../../components/ReportModal';
import Input from '../../components/Input';
import Datepicker from '../../components/Datepicker';
import {useDebounce} from '../LogSheets/hooks';
import {toast} from 'react-toastify';
import CustomPaginate from '../../components/PaginateComponent';
import GeneralModal from '../../components/GeneralModal/GeneralModal';
import FuelReceiptRow from '../../components/FuelReceiptRow';
import useLoader from '../../global/UseLoadingHandler';
import {useTranslation} from '../../hooks/useTranslation';

export default function FuelReceiptForm() {
  const {t} = useTranslation();
  const [showPDFModal, setShowPDFModal] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(window.location.search);
  const pageTab = urlParams.get('tab');
  const [currentTab, setCurrentTab] = useState(
    pageTab && pageTab !== '' ? pageTab : 1,
  );
  const [search, setSearch] = useState('');
  const [searchFuel, setSearchFuel] = useState('');
  const [fuelFilters, setFuelFilters] = useState({
    date_from: null,
    date_to: null,
    facility_id: null,
    equipment_id: null,
  });
  const [reportFilters, setReportFilters] = useState({
    date_from: null,
    date_to: null,
    facility_id: null,
    equipment_id: null,
  });
  const userInfo = useSelector(state => state?.rootReducer?.loginSlice?.user);
  const [reportModalStatus, setReportModalStatus] = useState(false);
  const [facilityOptions, setFacilityOptions] = useState([]);
  const [equipmentOptions, setEquipmentOptions] = useState([]);
  const [currentReport, setCurrentReport] = useState(null);
  const [dangerShow, setDangerShow] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const [getReceipt, {data: receiptData, isFetching: receiptIsLoading}] =
    useLazyGetFuelReceiptQuery();
  const [getFacility, {data: facilityData}] = useLazyGetFacilityQuery();

  const [getEquipment, {data: equipmentData}] = useLazyGetEquipmentQuery();

  const [getReportData, {data: reportData, isFetching: reportIsLoading}] =
    useLazyGetReportListQuery();

  const [
    removeReport,
    {isFetching: removeReportIsLoading, isSuccess: removeReportIsSuccess},
  ] = useRemoveReportMutation();

  const [
    checkPdf,
    {isFetching: checkPdfIsLoading, isSuccess: checkPdfIsSuccess},
  ] = useCheckReportMutation();

  const [openLink, {isFetching: openLinkIsLoading}] =
    useReportPdfLinkMutation();

  useLoader([
    reportIsLoading,
    removeReportIsLoading,
    checkPdfIsLoading,
    openLinkIsLoading,
    receiptIsLoading,
  ]);
  const debouncedSearchValue = useDebounce(search, 500);
  const debouncedSearchFuelValue = useDebounce(searchFuel, 500);

  useEffect(() => {
    getFacility();
    getEquipment({});
  }, []);

  useEffect(() => {
    if (facilityData) {
      // Hiding Fueling Equipment
      setFacilityOptions(
        facilityData
          ?.filter(item => item.name !== 'Fueling Equipment')
          ?.map(item => ({value: item.id, label: item.name})),
      );
    }
    if (equipmentData) {
      setEquipmentOptions(
        equipmentData?.data?.map(item => ({
          value: item.id,
          label: item.title,
        })),
      );
    }
  }, [facilityData, equipmentData]);

  useEffect(() => {
    if (currentTab == 2) {
      getReportData({
        facility_id: reportFilters?.facility_id,
        date_from: reportFilters?.date_from,
        date_to: reportFilters?.date_to,
        search: debouncedSearchFuelValue,
        type: 'pdf-fuel',
        page: currentPage,
      });
    } else {
      getFuelReceiptData();
    }
  }, [
    currentTab,
    fuelFilters,
    debouncedSearchValue,
    reportFilters,
    debouncedSearchFuelValue,
    currentPage,
  ]);

  useEffect(() => {
    const filteredArr = reportData?.data?.filter(el => el.file_name === 'wait');
    const interval = setInterval(() => {
      if (filteredArr?.length === 0) {
        clearInterval(interval);
      } else {
        getReportData({
          facility_id: reportFilters?.facility_id,
          date_from: reportFilters?.date_from,
          date_to: reportFilters?.date_to,
          search: debouncedSearchFuelValue,
          type: 'pdf-fuel',
        });
      }
    }, 10000);
    return () => {
      clearInterval(interval);
    };
  }, [reportData]);

  useEffect(() => {
    setFuelFilters(old => ({
      ...old,
      facility_id: urlParams.get('facility_id'),
      equipment_id: urlParams.get('equipment_id'),
      date_from: urlParams.get('date_from'),
      date_to: urlParams.get('date_to'),
    }));

    setReportFilters(old => ({
      ...old,
      facility_id: urlParams.get('facility_id_fuel'),
      equipment_id: urlParams.get('equipment_id_fuel'),
      date_from: urlParams.get('date_from_fuel'),
      date_to: urlParams.get('date_to_fuel'),
    }));
  }, []);

  useEffect(() => {
    if (removeReportIsSuccess) {
      dispatch(setLoader(false));
      toast.success(t('fuelReceiptForm.messages.reportDeleted'));
      setDangerShow(false);
    }
    if (checkPdfIsSuccess) {
      dispatch(setLoader(false));
      toast.success(t('fuelReceiptForm.messages.pdfRegenerated'));
      setShowPDFModal(false);
    }
  }, [removeReportIsSuccess, checkPdfIsSuccess]);

  useEffect(() => {
    navigate(
      `/receipt-form?tab=${currentTab}${urlParams.get('facility_id') ? `&facility_id=${urlParams.get('facility_id')}` : ''}${urlParams.get('equipment_id') ? `&equipment_id=${urlParams.get('equipment_id')}` : ''}${urlParams.get('date_from') ? `&date_from=${moment(urlParams.get('date_from'))}` : ''}${urlParams.get('date_to') ? `&date_to=${urlParams.get('date_to')}` : ''}`,
    );
  }, [currentTab]);

  const changeTab = tabId => {
    setCurrentTab(tabId);
    setCurrentPage(1);
    setReportFilters({
      date_from: null,
      date_to: null,
      facility_id: null,
      equipment_id: null,
    });
  };
  const getFuelReceiptData = () => {
    getReceipt({
      agency: userInfo?.agency?.id,
      isCompleted: currentTab,
      facility_id: fuelFilters?.facility_id,
      equipment_id: fuelFilters?.equipment_id,
      date_from: fuelFilters?.date_from,
      date_to: fuelFilters?.date_to,
      search: debouncedSearchValue,
      page: currentPage,
    });
  };

  const handleSearch = e => {
    setSearch(e.target.value);
  };

  const handleSearchFuel = e => {
    setSearchFuel(e.target.value);
  };

  const handleFilterDateFrom = date => {
    setCurrentPage(1);
    setFuelFilters(old => ({
      ...old,
      date_from: date ? moment(date).format('YYYY-MM-DD') : null,
    }));
    navigate(
      `/receipt-form?${urlParams.get('tab') ? `tab=${urlParams.get('tab')}` : currentTab}${urlParams.get('facility_id') ? `&facility_id=${urlParams.get('facility_id')}` : ''}${urlParams.get('equipment_id') ? `&equipment_id=${urlParams.get('equipment_id')}` : ''}${date ? `&date_from=${moment(date).format('YYYY-MM-DD')}` : ''}${urlParams.get('date_to') ? `&date_to=${urlParams.get('date_to')}` : ''}`,
    );
  };
  const handleFilterDateTo = date => {
    setCurrentPage(1);
    setFuelFilters(old => ({
      ...old,
      date_to: date ? moment(date).format('YYYY-MM-DD') : null,
    }));
    navigate(
      `/receipt-form?${urlParams.get('tab') ? `tab=${urlParams.get('tab')}` : currentTab}${urlParams.get('facility_id') ? `&facility_id=${urlParams.get('facility_id')}` : ''}${urlParams.get('equipment_id') ? `&equipment_id=${urlParams.get('equipment_id')}` : ''}${urlParams.get('date_from') ? `&date_from=${urlParams.get('date_from')}` : ''}${date ? `&date_to=${moment(date).format('YYYY-MM-DD')}` : ''}`,
    );
  };

  const handleFilterDateFromFuel = date => {
    setCurrentPage(1);
    setReportFilters(old => ({
      ...old,
      date_from: date ? moment(date).format('YYYY-MM-DD') : null,
    }));
    navigate(
      `/receipt-form?${urlParams.get('tab') ? `tab=${urlParams.get('tab')}` : currentTab}${urlParams.get('facility_id_fuel') ? `&facility_id_fuel=${urlParams.get('facility_id_fuel')}` : ''}${urlParams.get('equipment_id') ? `&equipment_id=${urlParams.get('equipment_id')}` : ''}${date ? `&date_from_fuel=${moment(date).format('YYYY-MM-DD')}` : ''}${urlParams.get('date_to_fuel') ? `&date_to_fuel=${urlParams.get('date_to_fuel')}` : ''}`,
    );
  };

  const handleFilterDateToFuel = date => {
    setCurrentPage(1);
    setReportFilters(old => ({
      ...old,
      date_to: date ? moment(date).format('YYYY-MM-DD') : null,
    }));
    navigate(
      `/receipt-form?${urlParams.get('tab') ? `tab=${urlParams.get('tab')}` : currentTab}${urlParams.get('facility_id_fuel') ? `&facility_id_fuel=${urlParams.get('facility_id_fuel')}` : ''}${urlParams.get('equipment_id') ? `&equipment_id=${urlParams.get('equipment_id')}` : ''}${urlParams.get('date_from_fuel') ? `&date_from_fuel=${urlParams.get('date_from_fuel')}` : ''}${date ? `&date_to_fuel=${moment(date).format('YYYY-MM-DD')}` : ''}`,
    );
  };
  const renderDanger = () => {
    return (
      <div className='modal'>
        <div className='modal__content'>
          <div className='modal__content-head'>
            <p>{t('fuelReceiptForm.modals.deleteReport')}</p>
            <img
              className='modal__content-exit'
              src='/assets/icons/close.svg'
              alt='close'
              onClick={() => setDangerShow(false)}
            />
          </div>
          <div
            className='modal__content-body'
            style={{display: 'flex', justifyContent: 'center'}}
          >
            {t('fuelReceiptForm.modals.deleteReportMessage')}
          </div>
          <div className='modal__content-bottom'>
            <button className='close' onClick={() => setDangerShow(false)}>
              {t('fuelReceiptRow.buttons.close')}
            </button>
            <button
              className='save danger'
              onClick={() => {
                removeReport(currentReport);
              }}
            >
              {t('fuelReceiptRow.buttons.yes')}
            </button>
          </div>
        </div>
        <div className='modal__bg'></div>
      </div>
    );
  };

  const handlePageClick = page => {
    setCurrentPage(page.selected + 1);
    window.scrollTo(0, 0);
  };

  const openReport = id => {
    const newWindow = window.open('', '_blank');
    if (!newWindow) {
      console.warn('Popup blocked');
      return;
    }
    openLink(id)
      .unwrap()
      .then(res => {
        if (res?.data?.url) {
          newWindow.location.href = `${res.data.url}?time=${Date.now()}`;
        } else {
          newWindow.close();
        }
      })
      .catch(error => {
        console.error('Failed to open the link:', error);
        newWindow.close();
      });
  };

  return (
    <Wrapper>
      {reportModalStatus && (
        <ReportModal
          title={t('fuelReceiptForm.modals.createReport')}
          exit={() => setReportModalStatus(false)}
          facility={facilityData?.map(item => ({
            value: item.id,
            label: item.name,
          }))}
          type={'fuel'}
        />
      )}
      {dangerShow && renderDanger()}
      <Helmet>
        <title>{t('fuelReceiptForm.pageTitle')}</title>
      </Helmet>
      <DashboardHeader
        title={t('fuelReceiptForm.title')}
        type={currentTab == 2 ? 'receiptForm' : ''}
        action={() => setReportModalStatus(true)}
      />
      <div className='receipt'>
        <div className='receipt__header'>
          <div
            className={`receipt__header-tab ${currentTab == 1 ? 'active' : ''}`}
            onClick={() => changeTab(1)}
          >
            {t('fuelReceiptForm.tabs.completed')}
          </div>
          <div
            className={`receipt__header-tab ${currentTab == 0 ? 'active' : ''}`}
            onClick={() => changeTab(0)}
          >
            {t('fuelReceiptForm.tabs.inProgress')}
          </div>
          <div
            className={`receipt__header-tab ${currentTab == 2 ? 'active' : ''}`}
            onClick={() => changeTab(2)}
          >
            {t('fuelReceiptForm.tabs.reports')}
          </div>
        </div>
        {currentTab == 2 ? (
          <div className='receipt__filters'>
            <Input
              type='text'
              name={'search'}
              id={'search'}
              onChange={handleSearchFuel}
              image='/assets/icons/search.svg'
              placeholder={t('fuelReceiptForm.searchPlaceholder')}
              defaultValue={search}
              maxwidth={200}
            />
            <Datepicker
              type={'from'}
              setValue={handleFilterDateFromFuel}
              value={urlParams.get('date_from_fuel')}
            />
            <Datepicker
              type={'to'}
              setValue={handleFilterDateToFuel}
              value={urlParams.get('date_to_fuel')}
            />
            <Select
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                  borderRadius: 8,
                  width: 200,
                  height: 42,
                  backgroundColor: '#F4F5F6',
                }),
              }}
              isClearable
              key={JSON.stringify(facilityOptions) + '1'}
              placeholder={t('fuelReceiptForm.filters.facility')}
              options={facilityOptions}
              defaultValue={
                facilityOptions?.filter(
                  item => item?.value == urlParams.get('facility_id_fuel'),
                )[0]
              }
              onChange={e => {
                setCurrentPage(1);
                setReportFilters(old => ({...old, facility_id: e?.value}));
                navigate(
                  `/receipt-form?${e?.value ? `&facility_id_fuel=${e?.value}` : ''}${urlParams.get('date_from_fuel') ? `&date_from_fuel=${urlParams.get('date_from_fuel')}` : ''}${urlParams.get('date_to_fuel') ? `&date_to_fuel=${urlParams.get('date_to_fuel')}` : ''}`,
                );
              }}
            />
          </div>
        ) : (
          <div className='receipt__filters'>
            <Input
              type='text'
              name={'search'}
              id={'search'}
              onChange={handleSearch}
              image='/assets/icons/search.svg'
              placeholder={t('fuelReceiptForm.searchPlaceholder')}
              defaultValue={search}
              maxwidth={200}
            />
            <Datepicker
              type={'from'}
              setValue={handleFilterDateFrom}
              value={urlParams.get('date_from')}
            />
            <Datepicker
              type={'to'}
              setValue={handleFilterDateTo}
              value={urlParams.get('date_to')}
            />
            <Select
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                  borderRadius: 8,
                  width: 200,
                  height: 42,
                  backgroundColor: '#F4F5F6',
                }),
              }}
              isClearable
              key={JSON.stringify(facilityOptions) + '1'}
              placeholder={t('fuelReceiptForm.filters.facility')}
              options={facilityOptions}
              defaultValue={
                facilityOptions?.filter(
                  item => item?.value == urlParams.get('facility_id'),
                )[0]
              }
              onChange={e => {
                setCurrentPage(1);
                setFuelFilters(old => ({...old, facility_id: e?.value}));
                navigate(
                  `/receipt-form?${urlParams.get('tab') ? `tab=${urlParams.get('tab')}` : currentTab}${e?.value ? `&facility_id=${e?.value}` : ''}${urlParams.get('equipment_id') ? `&equipment_id=${urlParams.get('equipment_id')}` : ''}${urlParams.get('date_from') ? `&date_from=${urlParams.get('date_from')}` : ''}${urlParams.get('date_to') ? `&date_to=${urlParams.get('date_to')}` : ''}`,
                );
              }}
            />
            <Select
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                  borderRadius: 8,
                  width: 200,
                  height: 42,
                  backgroundColor: '#F4F5F6',
                }),
              }}
              isClearable
              key={JSON.stringify(equipmentOptions) + '2'}
              placeholder={t('fuelReceiptForm.filters.equipment')}
              options={equipmentOptions}
              defaultValue={
                equipmentOptions?.filter(
                  item => item?.value == urlParams.get('equipment_id'),
                )[0]
              }
              onChange={e => {
                setCurrentPage(1);
                setFuelFilters(old => ({...old, equipment_id: e?.value}));
                navigate(
                  `/receipt-form?${urlParams.get('tab') ? `tab=${urlParams.get('tab')}` : currentTab}${urlParams.get('facility_id') ? `&facility_id=${urlParams.get('facility_id')}` : ''}${e?.value ? `&equipment_id=${e?.value}` : ''}${urlParams.get('date_from') ? `&date_from=${urlParams.get('date_from')}` : ''}${urlParams.get('date_to') ? `&date_to=${urlParams.get('date_to')}` : ''}`,
                );
              }}
            />
          </div>
        )}
        <div className='receipt__box'>
          <div className='receipt__table'>
            {currentTab == 2 ? (
              <div className='receipt__table-head'>
                <div className='receipt__table-th'>
                  {t('fuelReceiptForm.tableHeaders.name')}
                </div>
                <div className='receipt__table-th'>
                  {t('fuelReceiptForm.tableHeaders.timestamp')}
                </div>
                <div className='receipt__table-th'>
                  {t('fuelReceiptForm.tableHeaders.facility')}
                </div>
                <div className='receipt__table-th'>
                  {t('fuelReceiptForm.tableHeaders.year')}
                </div>
                <div className='receipt__table-th'>
                  {t('fuelReceiptForm.tableHeaders.monthFrom')}
                </div>
                <div className='receipt__table-th'>
                  {t('fuelReceiptForm.tableHeaders.monthTo')}
                </div>
                <div className='receipt__table-th'>
                  {t('fuelReceiptForm.tableHeaders.completedBy')}
                </div>
                <div className='receipt__table-th'>
                  {t('fuelReceiptForm.tableHeaders.download')}
                </div>
                <div className='receipt__table-th'></div>
              </div>
            ) : (
              <div className='receipt__table-head seven-cols'>
                <div className='receipt__table-th'>
                  {t('fuelReceiptForm.tableHeaders.facility')}
                </div>
                <div className='receipt__table-th'>
                  {t('fuelReceiptForm.tableHeaders.equipment')}
                </div>
                <div className='receipt__table-th'>
                  {t('fuelReceiptForm.tableHeaders.inspection')}
                </div>
                <div className='receipt__table-th'>
                  {t('fuelReceiptForm.tableHeaders.period')}
                </div>
                <div className='receipt__table-th'>
                  {t('fuelReceiptForm.tableHeaders.status')}
                </div>
                <div className='receipt__table-th'>
                  {t('fuelReceiptForm.tableHeaders.date')}
                </div>
                <div className='receipt__table-th'>
                  {t('fuelReceiptForm.tableHeaders.submittedBy')}
                </div>
              </div>
            )}

            <div className='receipt__table-body'>
              {currentTab == 2 ? (
                reportData?.data?.map(report => (
                  <div className='receipt__table-row' key={report?.id}>
                    <div className='receipt__table-bodyTh report'>
                      {report.name}
                    </div>
                    <div className='receipt__table-bodyTh report'>
                      {moment(report?.updated_at).format('MM/DD/YY HH:mm')}
                    </div>
                    <div className='receipt__table-bodyTh report'>
                      {report?.facility?.name}
                    </div>
                    <div className='receipt__table-bodyTh report'>
                      {report?.year}
                    </div>
                    <div className='receipt__table-bodyTh report'>
                      {report?.month_from}
                    </div>
                    <div className='receipt__table-bodyTh report'>
                      {report?.month_to}
                    </div>
                    <div className='receipt__table-bodyTh report'>
                      {report?.created_by?.name}
                    </div>
                    {report?.file_name === 'wait' ? (
                      <div className='report__td small'>
                        {t('fuelReceiptForm.status.generatingReport')}
                      </div>
                    ) : report?.file_name === 'has_errors' ? (
                      <div className='report__td small'>
                        {t('fuelReceiptForm.status.hasErrors')}
                      </div>
                    ) : report?.file_name && report?.file_name?.length > 0 ? (
                      <div
                        className='report__td'
                        rel='noreferrer'
                        onClick={() => openReport(report?.id)}
                      >
                        <img src='/assets/icons/Download.svg' alt='icon' />
                      </div>
                    ) : (
                      <div className='report__td'>
                        {t('fuelReceiptForm.status.noData')}
                      </div>
                    )}
                    <div className='receipt__table-bodyTh report delete'>
                      <img
                        src='/assets/icons/trash-2.svg'
                        alt='icon'
                        onClick={() => {
                          setDangerShow(true);
                          setCurrentReport(report.id);
                        }}
                      />

                      <img
                        width={24}
                        height={24}
                        src='/assets/icons/rotate-pdf.svg'
                        alt='icon'
                        onClick={() => {
                          setShowPDFModal(true);
                          setCurrentReport(report.id);
                        }}
                      />
                    </div>
                  </div>
                ))
              ) : receiptData?.data?.length ? (
                receiptData?.data?.map(receipt => (
                  <FuelReceiptRow
                    receipt={receipt}
                    userInfo={userInfo}
                    getFuelReceiptData={getFuelReceiptData}
                    currentTab={currentTab}
                  />
                ))
              ) : (
                <div className='emptyList'>
                  <img src='/assets/empty-list.png' alt='empty' />
                  <p>{t('fuelReceiptForm.messages.noData')}</p>
                </div>
              )}
            </div>
          </div>
          {currentTab == 2 ? (
            reportData?.data?.length ? (
              <CustomPaginate
                pageCount={reportData?.pages}
                handlePageClick={handlePageClick}
                currentPage={currentPage}
              />
            ) : null
          ) : receiptData?.data?.length ? (
            <CustomPaginate
              pageCount={receiptData?.pages}
              handlePageClick={handlePageClick}
              currentPage={currentPage}
            />
          ) : null}
        </div>
        {showPDFModal && (
          <GeneralModal
            title={t('fuelReceiptForm.modals.regeneratePdf')}
            onClose={() => setShowPDFModal(false)}
            onSubmit={() => checkPdf(currentReport)}
          >
            {t('fuelReceiptForm.modals.regeneratePdfMessage')}
          </GeneralModal>
        )}
      </div>
    </Wrapper>
  );
}
